import * as moment from 'moment';
import { Dashboard, DashboardFiltersChangeHandler, WidgetInstance } from './dashboard.service';
import type { EntityService, Enumeration, EnumerationValue } from '../Shell/PandoraSolutionBuilderAgent/services/entity-service';
import { MxFilterOperatorsFactory } from '../Shell/PandoraSolutionBuilderAgent/modules/DataQueryModel/column-filters/mx-grid-column-filter-operators-factory';
import { SearchCondition, SearchGroup, SearchLogicalOperator } from '../Shell/PandoraSolutionBuilderAgent/model/search-query/search-form.contracts';
import { ColumnDefinitionService } from '../Shell/PandoraSolutionBuilderAgent/services/column-definition-service';
import type { DataQuery, DataQueryColumn } from '../Shell/PandoraSolutionBuilderAgent/modules/DataQueryModel/contracts';
import { InteractiveFilter, InteractiveFilterForLayoutDesigner } from './InteractiveFilter.model';
import { MxObjectPropertyType } from '../Shell/Pandora/model/object';
import type { I18n } from '../i18n/mx-internationalization';
import type { SchemaService } from '../Shell/PandoraSolutionBuilderAgent/services/schema-service';
import { DependentFiltersGroup } from './dependentFiltersGroup';
import { DependentFilter } from './dependentFilter.model';
import type { DataQueryService } from '../App/services/data-query-service';
import { EventHandler, HandlerCollection } from '../Utils/mx-handler.collection';

export interface PDRReportsFilterOptionPickupItem extends EnumerationValue {
	AttributeType: number;
	Name: string;
	Filter: string;
}

type PDRReportsFilterOptionEnum = Enumeration<PDRReportsFilterOptionPickupItem>;

export interface DashboardFiltersValues {
	keyword?: string;
	dependent?: {
		[id: string]: string[];
	};
	interactive?: {
		[id: string]: unknown[];
	};
}

export type DefaultValueType = 'none' | 'static' | 'user-related';

export interface InteractiveFilterParams<DefaultValue = unknown[] | null | undefined | string> {
	ColumnName: string;
	ReportFilter: number; //PDRReportsFilterOptionPickup
	Required?: boolean;
	IsMultiPicker?: boolean;
	DefaultValue?: DefaultValue;
	DefaultValueType?: DefaultValueType;
}

export class DashboardFiltersService {
	private readonly userRelatedFiltersDataQueryId = 'ac4fa82d-587c-cc74-6282-08dd49bf54b9';
	private userRelatedData?: Record<string, unknown>;

	static readonly $inject = [
		'mx.sb.Agent.DataQuery.FilterOperatorsFactory',
		'mx.SolutionBuilderAgent.EntityService',
		'mx.SolutionBuilderAgent.mxColumnDefinitionService',
		'mx.internationalization',
		'mx.SolutionBuilderAgent.SchemaService',
		'mx.SolutionBuilderAgent.DataQueryService',
	];

	constructor(
		private readonly filterOperatorsFactory: MxFilterOperatorsFactory,
		private readonly entityService: EntityService,
		private readonly columnDefinitionService: ReturnType<typeof ColumnDefinitionService>,
		private readonly i18n: I18n,
		private readonly schemaService: SchemaService,
		private readonly dataQueryService: ReturnType<typeof DataQueryService>
	) {}

	public async getInteractiveFiltersOfWidgetInstance(widgetInstance: WidgetInstance) {
		if (!widgetInstance.dataModel.WidgetConfiguration.DataQueryObjId) {
			return [];
		}

		const dataQuery = (await this.columnDefinitionService.getGridDefinitionById(
			widgetInstance.dataModel.WidgetConfiguration.DataQueryObjId,
			widgetInstance.dataModel.WidgetConfiguration.DatasetViewObjId
		)) as DataQuery;

		if (Array.isArray(widgetInstance.dataModel.WidgetConfiguration.DynamicFilters)) {
			return await this.getInteractiveFiltersFromConfig(widgetInstance, widgetInstance.dataModel.WidgetConfiguration.DynamicFilters, dataQuery);
		}

		console.error(`Dashboard Widget Instance: "${widgetInstance.title}": "dataModel.WidgetConfiguration.DynamicFilters" must be an array`);
		return [];
	}

	public async getReportsFilterOptionPickupEnum() {
		return await this.entityService.getEnumeration<PDRReportsFilterOptionEnum>('PDRReportsFilterOptionPickup');
	}

	public async getInteractiveFiltersFromConfig(widgetInstance: WidgetInstance, filtersParams: InteractiveFilterParams[] = [], dataQuery: DataQuery) {
		const reportsFilterEnum = await this.getReportsFilterOptionPickupEnum();
		const userRelatedData = await this.loadUserRelatedData();

		const filtersMap = new Map(
			reportsFilterEnum.Values?.map((value) => {
				return [
					value.Value,
					{ title: value.DisplayString, type: value.AttributeType, value: value.Value, position: value.Position, name: value.Name, subfilters: value.Filter },
				];
			})
		);

		return filtersParams
			.map((dynamicFilter) => {
				const column = dataQuery.getColumnByName(dynamicFilter.ColumnName) as DataQueryColumn;

				if (!column) {
					return;
				}

				const filterConfig = filtersMap.get(dynamicFilter.ReportFilter);
				if (!filterConfig) {
					console.error(`Filter with Value=${dynamicFilter.ReportFilter} doesn't exist in PDRReportsFilterOptionPickup`);
					return;
				}

				const interactiveFilter = new InteractiveFilter(
					filterConfig.name,
					filterConfig.title,
					column,
					widgetInstance,
					this.filterOperatorsFactory.operators,
					filterConfig.subfilters
				);
				interactiveFilter.position = filterConfig.position;

				interactiveFilter.required = dynamicFilter.Required ?? false;
				interactiveFilter.isMultiPicker = dynamicFilter.IsMultiPicker ?? true;

				if (this.isStaticDynamicFilter(dynamicFilter)) {
					interactiveFilter.setDefaultValue(dynamicFilter.DefaultValue, '');
				} else if (this.isUserRelatedDynamicFilter(dynamicFilter) && typeof dynamicFilter.DefaultValue === 'string') {
					const value = userRelatedData[`${dynamicFilter.DefaultValue}_Value`] ?? userRelatedData[`${dynamicFilter.DefaultValue}`];
					interactiveFilter.setDefaultValue([value], '');
				}
				return interactiveFilter;
			})
			.filter((value) => value) as InteractiveFilter[];
	}

	public isNonValueDynamicFilter(dynamicFilter: InteractiveFilterParams): dynamicFilter is InteractiveFilterParams<undefined> {
		return dynamicFilter.DefaultValueType === 'none';
	}

	public isStaticDynamicFilter(dynamicFilter: InteractiveFilterParams): dynamicFilter is InteractiveFilterParams<unknown[] | null | undefined> {
		return dynamicFilter.DefaultValueType === 'static';
	}

	public isUserRelatedDynamicFilter(dynamicFilter: InteractiveFilterParams): dynamicFilter is InteractiveFilterParams<string | undefined> {
		return dynamicFilter.DefaultValueType === 'user-related';
	}

	public async initDashboardFilters(dashboard: Dashboard, initialFilterValues?: DashboardFiltersValues) {
		dashboard.filters.hasDatasetRelatedWidgets = this.dashboardHasDatasetRelatedWidgets(dashboard);
		if (dashboard.filters.hasDatasetRelatedWidgets) {
			dashboard.filters.keyword = initialFilterValues?.keyword ?? '';
			dashboard.filters.interactive = await this.getInteractiveFilters(dashboard, initialFilterValues);
		} else {
			dashboard.filters.keyword = '';
			dashboard.filters.interactive = [];
		}

		//TODO: reinit for dependent is not implemented; It should be updated on change dashboard configuration
		dashboard.filters.dependent = dashboard.filters.dependent || new DependentFiltersGroup(this.schemaService, this.filterOperatorsFactory, dashboard, initialFilterValues);

		this.applyFilters(dashboard, dashboard.widgetInstances);
		this.updateFiltersStatus(dashboard);

		this.addInteractiveFiltersChangeHandler(dashboard);
	}

	private addInteractiveFiltersChangeHandler(dashboard: Dashboard) {
		const previousHandlers: EventHandler[] = dashboard.filters.changeHandler?.unsubscribe() ?? [];

		const changeHandlers = new HandlerCollection<void, void>();
		const unsubscribers: Array<() => EventHandler | void> = [];
		let valueChanged = false;
		let stringValueChanged = false;
		let widgetInstances: WidgetInstance[] = [];

		const handler = $.debounce(50, () => {
			if (valueChanged) {
				this.applyFilters(dashboard, widgetInstances);
			}
			if (stringValueChanged) {
				this.updateFiltersStatus(dashboard);
			}

			if (valueChanged) {
				changeHandlers.execute();
			}

			valueChanged = false;
			stringValueChanged = false;
			widgetInstances = [];
		});

		const filtersChangeHandler: DashboardFiltersChangeHandler = {
			onChange: (handler: EventHandler) => {
				const unsubscribe = changeHandlers.add(handler);
				const unsubscriber = () => {
					unsubscribe();
					return handler;
				};
				unsubscribers.push(unsubscriber);
			},
			unsubscribe: () => {
				return unsubscribers.map((unsubscribe) => unsubscribe()).filter((handler) => handler) as EventHandler[];
			},
			requestChange({ valueChanged: vc = false, stringValueChanged: svc = false, widgetInstances: wis = [] }) {
				valueChanged ||= vc;
				stringValueChanged ||= svc;
				widgetInstances = widgetInstances.concat(wis.filter((widgetInstance) => !widgetInstances.includes(widgetInstance)));
				handler();
			},
		};

		dashboard.filters.interactive.forEach((interactiveFilter) => {
			const unsubscribe = interactiveFilter.onChange((options) => {
				filtersChangeHandler.requestChange({
					valueChanged: options.valueChanged,
					stringValueChanged: options.stringValueChanged,
					widgetInstances: options.value.map((interactiveFilterGroup) => interactiveFilterGroup.widgetInstance),
				});
			});
			unsubscribers.push(unsubscribe);
		});

		previousHandlers.forEach((handler) => filtersChangeHandler.onChange(handler));

		dashboard.filters.changeHandler = filtersChangeHandler;
	}

	public async reinitDashboardFilters(dashboard: Dashboard) {
		await this.initDashboardFilters(dashboard, dashboard.filters.values);
	}

	public dashboardHasDatasetRelatedWidgets(dashboard: Dashboard) {
		return dashboard.widgetInstances.some((widgetInstance) => !widgetInstance.hidden && widgetInstance.widget?.DatasetRelated);
	}

	public async getInteractiveFilters(dashboard: Dashboard, initialFilterValues?: DashboardFiltersValues) {
		const interactiveFilters: InteractiveFilter[] = [];
		await Promise.all(
			dashboard.widgetInstances
				.filter((widgetInstance) => !widgetInstance.hidden && widgetInstance.widget?.DatasetRelated)
				.map(async (widgetInstance) => {
					const filters = await this.getInteractiveFiltersOfWidgetInstance(widgetInstance);

					filters.forEach((filter) => {
						const value = initialFilterValues?.interactive?.[filter.name];
						if (typeof value !== 'undefined') {
							if (filter.type === MxObjectPropertyType.Date.name && Array.isArray(value)) {
								value.forEach((dateString, index) => {
									if (typeof dateString === 'string' && dateString.length) {
										const date = moment(dateString);
										if (date.isValid()) {
											value[index] = moment(dateString).toDate().toISOString();
										}
									}
								});
							}
							filter.setValue(value, '');
						}
						const filterToMerge = interactiveFilters.find((existFilter) => existFilter.isMergeable(filter));

						if (filterToMerge) {
							filterToMerge.merge(filter);
						} else {
							interactiveFilters.push(filter);
						}
					});
				})
		);

		interactiveFilters.sort((a, b) => {
			if (typeof a.position === 'number' && typeof b.position === 'number') {
				return a.position - b.position;
			}

			if (typeof a.position === 'number') {
				return -1;
			}

			if (typeof b.position === 'number') {
				return 1;
			}

			return b.title.localeCompare(a.title);
		});

		return interactiveFilters;
	}

	private updateFiltersValues(dashboard: Dashboard) {
		dashboard.filters.hasNonDefaultValues = false;

		if (dashboard.filters.keyword) {
			dashboard.filters.values.keyword = dashboard.filters.keyword;
			dashboard.filters.hasNonDefaultValues = true;
		} else {
			delete dashboard.filters.values.keyword;
		}

		const dependentValues = dashboard.filters.dependent?.getValues() ?? {};
		if (Object.keys(dependentValues).length) {
			dashboard.filters.values.dependent = dependentValues;
		} else {
			delete dashboard.filters.values.dependent;
		}

		dashboard.filters.interactive.forEach((interactiveFilter) => {
			if (interactiveFilter.hasValue() && !interactiveFilter.isDefaultValue()) {
				dashboard.filters.values.interactive = dashboard.filters.values.interactive || {};
				if (interactiveFilter.type === MxObjectPropertyType.Date.name) {
					dashboard.filters.values.interactive[interactiveFilter.name] = interactiveFilter.value.map((dateString) => {
						return typeof dateString === 'string' && dateString.length ? moment(dateString).utc(true).toISOString().substring(0, 10) : dateString;
					});
				} else {
					dashboard.filters.values.interactive[interactiveFilter.name] = interactiveFilter.value;
				}
				dashboard.filters.hasNonDefaultValues = true;
			} else if (typeof dashboard.filters.values.interactive?.[interactiveFilter.name] !== 'undefined') {
				delete dashboard.filters.values.interactive[interactiveFilter.name];
			}
		});

		if (Object.keys(dashboard.filters.values.interactive ?? {}).length === 0) {
			delete dashboard.filters.values.interactive;
		}
	}

	public getFiltersRouteParams(dashboard: Dashboard): string {
		return Object.keys(dashboard.filters.values).length > 0 ? JSON.stringify(dashboard.filters.values) : '';
	}

	public parseFiltersRoute(filter: string): DashboardFiltersValues {
		try {
			const parsed = JSON.parse(filter);
			if (typeof parsed !== 'object') {
				return {};
			}
			if (typeof parsed.keyword !== 'string') {
				delete parsed.keyword;
			}
			if (typeof parsed.interactive !== 'object') {
				delete parsed.interactive;
			}
			if (typeof parsed.dependent !== 'object') {
				delete parsed.dependent;
			}
			return parsed;
		} catch {
			return {};
		}
	}

	private getInteractiveConditions(dashboard: Dashboard, widgetInstance: WidgetInstance): SearchCondition[] {
		const conditions: SearchCondition[] = [];

		dashboard.filters.interactive.forEach((interactiveFilter) => {
			const interactiveFilterGroups = interactiveFilter.getSearchConditions();
			interactiveFilterGroups.forEach((interactiveFilterGroup) => {
				if (interactiveFilterGroup.widgetInstance === widgetInstance) {
					conditions.push(...interactiveFilterGroup.conditions);
				}
			});
		});

		return conditions;
	}

	private getWidgetInstanceWithEmptyRequiredInteractiveFilters(dashboard: Dashboard): WidgetInstance[] {
		const widgetInstances: WidgetInstance[] = [];
		dashboard.filters.interactive
			.filter((interactiveFilter) => interactiveFilter.required && !interactiveFilter.hasValue())
			.forEach((interactiveFilter) => {
				const interactiveFilterGroups = interactiveFilter.getSearchConditions();
				interactiveFilterGroups.forEach((interactiveFilterGroup) => {
					if (!widgetInstances.includes(interactiveFilterGroup.widgetInstance)) {
						widgetInstances.push(interactiveFilterGroup.widgetInstance);
					}
				});
			});
		return widgetInstances;
	}

	public getConditionsForWidgetInstance(dashboard: Dashboard, widgetInstance: WidgetInstance): SearchGroup {
		const dependentGroup = dashboard.filters.dependent?.getSearchGroupForWidgetInstance(widgetInstance);
		const staticGroup = widgetInstance.dataModel.WidgetConfiguration.StaticFilters;
		const groups: SearchGroup[] = [dependentGroup, staticGroup].filter((group) => group?.Conditions?.length || group?.Groups?.length) as SearchGroup[];

		return {
			Groups: groups,
			Conditions: this.getInteractiveConditions(dashboard, widgetInstance),
			LogicalOperator: SearchLogicalOperator.And,
		};
	}

	public async getColumnSearchForWidgetInstance(dashboard: Dashboard, widgetInstance: WidgetInstance): Promise<SearchGroup> {
		const conditions = this.getInteractiveConditions(dashboard, widgetInstance);
		const dependentConditions = (await dashboard.filters.dependent?.geConditionsForWidgetInstance(widgetInstance)) ?? [];

		return {
			Groups: [],
			Conditions: conditions.concat(dependentConditions),
			LogicalOperator: SearchLogicalOperator.And,
		};
	}

	public updateFiltersStatus(dashboard: Dashboard) {
		const filters = dashboard.filters.interactive.map((filter) => filter.stringValue).filter((value) => value);

		if (dashboard.filters.keyword) {
			filters.unshift(this.filterOperatorsFactory.operators.Equals.getFullLabel(this.i18n.get('components.common.keywords'), [dashboard.filters.keyword]));
		}

		dashboard.filters.status = filters.length ? this.i18n.getFormatted(`shell.dashboards.filterStatus`, filters.map((f) => `[${f}]`).join(', ')) : undefined;

		this.updateFiltersValues(dashboard);
	}

	private mergeKeywords(staticKeyword?: string | null, dynamicKeyword?: string | null): string {
		staticKeyword = (staticKeyword ?? '').toLowerCase();
		dynamicKeyword = (dynamicKeyword ?? '').toLowerCase();
		if (staticKeyword.includes(dynamicKeyword)) {
			return staticKeyword;
		} else if (dynamicKeyword.includes(staticKeyword)) {
			return dynamicKeyword;
		} else {
			return '__DO_NOT_RETURN_ANYTHING__';
		}
	}

	public populateInteractiveFilters(widgetInstances: WidgetInstance[]) {
		const filteredInstances = widgetInstances.filter((widgetInstance) => !widgetInstance.hidden);
		const filters = filteredInstances.length ? this.getDashboardInteractiveFiltersForLayoutDesigner(filteredInstances[0].dashboard) : undefined;
		for (const widgetInstance of filteredInstances) {
			widgetInstance.dataModel.Context.AllDashboardInteractiveFilters = filters;
		}
	}

	private getDashboardInteractiveFiltersForLayoutDesigner(dashboard: Dashboard): InteractiveFilterForLayoutDesigner[] {
		return dashboard.filters.interactive.map((f: InteractiveFilter) => ({
			filterValue: f.value,
			name: f.name,
			operator: f.operator,
		}));
	}

	private applyFilters(dashboard: Dashboard, widgetInstances: WidgetInstance[]) {
		widgetInstances
			.filter((widgetInstance) => widgetInstance.widget?.DatasetRelated && !widgetInstance.hidden)
			.forEach((widgetInstance) => {
				const group = this.getConditionsForWidgetInstance(dashboard, widgetInstance);
				const currentDashboardFilters = widgetInstance.dataModel.Context.DashboardFilters;

				if (currentDashboardFilters?.Conditions?.length !== group.Conditions?.length || JSON.stringify(currentDashboardFilters) !== JSON.stringify(group)) {
					widgetInstance.dataModel.Context.DashboardFilters = group;
				}

				const keyword = this.mergeKeywords(widgetInstance.dataModel.WidgetConfiguration.SearchKeyword, dashboard.filters.keyword);

				if ((widgetInstance.dataModel.Context.KeywordFilter ?? '') !== keyword) {
					widgetInstance.dataModel.Context.KeywordFilter = keyword;
				}
			});

		this.populateInteractiveFilters(dashboard.widgetInstances);

		dashboard.widgetInstances.forEach((widgetInstance) => {
			widgetInstance.hasEmptyRequiredInteractiveFilter = false;
		});
		this.getWidgetInstanceWithEmptyRequiredInteractiveFilters(dashboard).forEach((widgetInstance) => {
			widgetInstance.hasEmptyRequiredInteractiveFilter = true;
		});
	}

	public applyDependentFilters(dashboard: Dashboard, dependentFilter: DependentFilter) {
		this.applyFilters(
			dashboard,
			dependentFilter.dependencies.map(({ widgetInstance }) => widgetInstance)
		);
		this.updateFiltersStatus(dashboard);
	}

	public applyKeywordFilter(dashboard: Dashboard, keyword: string) {
		dashboard.filters.keyword = keyword;
		dashboard.filters.changeHandler?.requestChange({ valueChanged: true, stringValueChanged: true, widgetInstances: dashboard.widgetInstances });
	}

	public async getUserRelatedDataQuery() {
		return (await this.columnDefinitionService.getGridDefinitionById(this.userRelatedFiltersDataQueryId)) as DataQuery;
	}

	private async loadUserRelatedData() {
		this.userRelatedData = this.userRelatedData || (await this.dataQueryService.getDataQueryData(this.userRelatedFiltersDataQueryId))[0];
		return this.userRelatedData ?? {};
	}

	public cleanUpFilters(dashboard: Dashboard) {
		dashboard.filters.keyword = '';
		dashboard.filters.interactive.forEach((filter) => filter.resetToDefaultValue());
		dashboard.filters.changeHandler?.requestChange({ valueChanged: true, stringValueChanged: true, widgetInstances: dashboard.widgetInstances });
	}
}
